import React, { useRef, useState, useCallback, useMemo } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Keyboard,
} from 'react-native';
import { createStyles } from './DbSearchBar.styles';
import type { DbSearchBarProps, SearchBarIconConfig } from './DbSearchBar.types';
import DbIcon from '../DbIcon';

// 默认图标配置
const DEFAULT_ICONS = {
  search: { name: 'search-outline', family: 'Ionicons' as const, size: 18 },
  clear: { name: 'close-circle', family: 'Ionicons' as const, size: 16 },
  camera: { name: 'camera-outline', family: 'Ionicons' as const, size: 20 },
  scan: { name: 'scan-outline', family: 'Ionicons' as const, size: 20 },
  voice: { name: 'mic-outline', family: 'Ionicons' as const, size: 20 },
};

// 渲染图标的辅助函数
const renderIcon = (
  config: SearchBarIconConfig | undefined,
  defaultConfig: SearchBarIconConfig,
  defaultColor: string
) => {
  const mergedConfig = {
    ...defaultConfig,
    ...config,
  };
  
  return (
    <DbIcon
      name={mergedConfig.name || defaultConfig.name!}
      family={mergedConfig.family || defaultConfig.family}
      size={mergedConfig.size || defaultConfig.size}
      color={mergedConfig.color || defaultColor}
    />
  );
};

// 清除按钮图标 (使用圆形背景)
const ClearIconButton: React.FC<{
  config?: SearchBarIconConfig;
  defaultColor: string;
  onPress: () => void;
}> = ({ config, defaultColor, onPress }) => {
  const iconConfig = {
    ...DEFAULT_ICONS.clear,
    ...config,
  };
  
  return (
    <TouchableOpacity onPress={onPress} style={{ padding: 4 }}>
      <DbIcon
        name={iconConfig.name || DEFAULT_ICONS.clear.name}
        family={iconConfig.family || DEFAULT_ICONS.clear.family}
        size={iconConfig.size || DEFAULT_ICONS.clear.size}
        color={iconConfig.color || defaultColor}
      />
    </TouchableOpacity>
  );
};

const DbSearchBar: React.FC<DbSearchBarProps> = ({
  value,
  defaultValue = '',
  placeholder = '搜索商品',
  placeholderTextColor,
  onChangeText,
  onSubmit,
  onPress,
  onFocus,
  onBlur,
  onClear,
  // 左侧
  leftIcon,
  leftExtra,
  onLeftIconPress,
  onLeftExtraPress,
  // 右侧
  rightIcons,
  rightExtra,
  onRightExtraPress,
  // 内置功能
  showCamera = false,
  onCameraPress,
  showScan = false,
  onScanPress,
  showVoice = false,
  onVoicePress,
  showClear = true,
  // 图标配置
  icons,
  // 样式
  disabled = false,
  autoFocus = false,
  shape = 'round',
  inputBackground,
  background,
  iconColor,
  iconSize = 18,
  style,
  inputContainerStyle,
  inputStyle,
  searchButtonText = '搜索',
  showSearchButton = false,
  searchButtonStyle,
  searchButtonTextStyle,
  // 其他 TextInput 属性
  ...textInputProps
}) => {
  const styles = useMemo(() => createStyles('light'), []);
  const inputRef = useRef<TextInput>(null);
  const [inputValue, setInputValue] = useState(defaultValue);
  const [isFocused, setIsFocused] = useState(false);

  // 默认图标颜色
  const defaultIconColor = iconColor || styles.iconDefault.color || '#999';

  // 受控/非受控模式
  const currentValue = value !== undefined ? value : inputValue;
  const hasValue = currentValue.length > 0;

  // 处理文本变化
  const handleChangeText = useCallback((text: string) => {
    if (value === undefined) {
      setInputValue(text);
    }
    onChangeText?.(text);
  }, [value, onChangeText]);

  // 处理清空
  const handleClear = useCallback(() => {
    if (value === undefined) {
      setInputValue('');
    }
    onChangeText?.('');
    onClear?.();
    inputRef.current?.focus();
  }, [value, onChangeText, onClear]);

  // 处理提交
  const handleSubmit = useCallback(() => {
    Keyboard.dismiss();
    onSubmit?.(currentValue);
  }, [currentValue, onSubmit]);

  // 处理聚焦
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocus?.();
  }, [onFocus]);

  // 处理失焦
  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onBlur?.();
  }, [onBlur]);

  // 处理点击 (disabled 模式)
  const handlePress = useCallback(() => {
    if (disabled && onPress) {
      onPress();
    }
  }, [disabled, onPress]);

  // 渲染左侧图标
  const renderLeftIcon = () => {
    // 如果传入了自定义 leftIcon ReactNode，直接使用
    if (leftIcon) {
      if (onLeftIconPress) {
        return (
          <TouchableOpacity onPress={onLeftIconPress} style={styles.leftIcon}>
            {leftIcon}
          </TouchableOpacity>
        );
      }
      return <View style={styles.leftIcon}>{leftIcon}</View>;
    }

    // 使用 DbIcon 渲染默认搜索图标
    const searchIconConfig = {
      ...DEFAULT_ICONS.search,
      size: iconSize,
      ...icons?.search,
    };

    const icon = (
      <DbIcon
        name={searchIconConfig.name}
        family={searchIconConfig.family}
        size={searchIconConfig.size}
        color={searchIconConfig.color || defaultIconColor}
      />
    );

    if (onLeftIconPress) {
      return (
        <TouchableOpacity onPress={onLeftIconPress} style={styles.leftIcon}>
          {icon}
        </TouchableOpacity>
      );
    }

    return <View style={styles.leftIcon}>{icon}</View>;
  };

  // 渲染右侧内置图标
  const renderRightIcons = () => {
    const iconElements: React.ReactNode[] = [];

    // 清除按钮
    if (showClear && hasValue && !disabled) {
      iconElements.push(
        <ClearIconButton
          key="clear"
          config={icons?.clear}
          defaultColor="#ccc"
          onPress={handleClear}
        />
      );
    }

    // 分隔线
    const needDivider = (showCamera || showScan || showVoice || (rightIcons && rightIcons.length > 0));
    if (iconElements.length > 0 && needDivider) {
      iconElements.push(<View key="divider" style={styles.iconDivider} />);
    }

    // 语音按钮
    if (showVoice) {
      const voiceIconConfig = {
        ...DEFAULT_ICONS.voice,
        size: iconSize,
        ...icons?.voice,
      };
      iconElements.push(
        <TouchableOpacity key="voice" onPress={onVoicePress} style={styles.iconButton}>
          <DbIcon
            name={voiceIconConfig.name}
            family={voiceIconConfig.family}
            size={voiceIconConfig.size}
            color={voiceIconConfig.color || defaultIconColor}
          />
        </TouchableOpacity>
      );
    }

    // 拍照按钮
    if (showCamera) {
      const cameraIconConfig = {
        ...DEFAULT_ICONS.camera,
        size: iconSize,
        ...icons?.camera,
      };
      iconElements.push(
        <TouchableOpacity key="camera" onPress={onCameraPress} style={styles.iconButton}>
          <DbIcon
            name={cameraIconConfig.name}
            family={cameraIconConfig.family}
            size={cameraIconConfig.size}
            color={cameraIconConfig.color || defaultIconColor}
          />
        </TouchableOpacity>
      );
    }

    // 扫一扫按钮
    if (showScan) {
      const scanIconConfig = {
        ...DEFAULT_ICONS.scan,
        size: iconSize,
        ...icons?.scan,
      };
      iconElements.push(
        <TouchableOpacity key="scan" onPress={onScanPress} style={styles.iconButton}>
          <DbIcon
            name={scanIconConfig.name}
            family={scanIconConfig.family}
            size={scanIconConfig.size}
            color={scanIconConfig.color || defaultIconColor}
          />
        </TouchableOpacity>
      );
    }

    // 自定义右侧图标
    if (rightIcons) {
      rightIcons.forEach((icon, index) => {
        iconElements.push(
          <View key={`custom-${index}`} style={styles.iconButton}>
            {icon}
          </View>
        );
      });
    }

    if (iconElements.length === 0) return null;

    return <View style={styles.rightIconsContainer}>{iconElements}</View>;
  };

  // 渲染搜索按钮
  const renderSearchButton = () => {
    if (!showSearchButton) return null;

    return (
      <TouchableOpacity
        style={[styles.searchButton, searchButtonStyle]}
        onPress={handleSubmit}
        activeOpacity={0.7}
      >
        <Text style={[styles.searchButtonText, searchButtonTextStyle]}>
          {searchButtonText}
        </Text>
      </TouchableOpacity>
    );
  };

  const containerStyle = [
    styles.container,
    background && { backgroundColor: background },
    style,
  ];

  const inputContainerStyles = [
    styles.inputContainer,
    shape === 'square' && styles.inputContainerSquare,
    isFocused && styles.inputContainerFocused,
    inputBackground && { backgroundColor: inputBackground },
    inputContainerStyle,
  ];

  const inputStyles = [
    styles.input,
    disabled && styles.inputDisabled,
    inputStyle,
  ];

  return (
    <View style={containerStyle}>
      {/* 左侧额外区域 */}
      {leftExtra && (
        <TouchableOpacity
          style={styles.leftExtra}
          onPress={onLeftExtraPress}
          disabled={!onLeftExtraPress}
        >
          {leftExtra}
        </TouchableOpacity>
      )}

      {/* 输入框容器 */}
      <TouchableWithoutFeedback onPress={handlePress} disabled={!disabled}>
        <View style={inputContainerStyles}>
          {renderLeftIcon()}

          <TextInput
            ref={inputRef}
            style={inputStyles}
            value={currentValue}
            placeholder={placeholder}
            placeholderTextColor={placeholderTextColor || styles.iconDefault.color}
            onChangeText={handleChangeText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onSubmitEditing={handleSubmit}
            returnKeyType="search"
            autoFocus={autoFocus}
            editable={!disabled}
            pointerEvents={disabled ? 'none' : 'auto'}
            {...textInputProps}
          />

          {renderRightIcons()}
        </View>
      </TouchableWithoutFeedback>

      {/* 右侧额外区域 */}
      {rightExtra && (
        <TouchableOpacity
          style={styles.rightExtra}
          onPress={onRightExtraPress}
          disabled={!onRightExtraPress}
        >
          {rightExtra}
        </TouchableOpacity>
      )}

      {/* 搜索按钮 */}
      {renderSearchButton()}
    </View>
  );
};

export default DbSearchBar;
export type { DbSearchBarProps, SearchBarIconConfig, SearchBarIconsConfig } from './DbSearchBar.types';
